<script setup lang="ts">
import { computed } from 'vue';
import type { ActivityStatus } from '@/composables/useActivityLabels';

interface ProgressData {
    current?: number;
    total?: number;
    percentage?: number;
}

const props = defineProps<{
    progress: ProgressData | null;
    status: ActivityStatus;
}>();

const isVisible = computed(() =>
    ['pending', 'running', 'completing'].includes(props.status),
);

const isIndeterminate = computed(() => props.status === 'pending');

const percent = computed<number>(() => {
    if (!props.progress) {
        return 0;
    }

    if (typeof props.progress.percentage === 'number') {
        return props.progress.percentage;
    }

    if (props.progress.total && props.progress.total > 0) {
        return Math.round(
            ((props.progress.current ?? 0) / props.progress.total) * 100,
        );
    }

    return 0;
});

const hasCounter = computed(
    () =>
        !isIndeterminate.value &&
        props.progress?.total != null &&
        props.progress.total > 0,
);
</script>

<template>
    <div v-if="isVisible" class="mt-4 space-y-1.5">
        <!-- Track -->
        <div
            class="relative h-1 w-full overflow-hidden rounded-full bg-secondary"
            role="progressbar"
            :aria-valuenow="isIndeterminate ? undefined : percent"
            :aria-valuemin="0"
            :aria-valuemax="100"
            aria-label="Progression de l'activité"
        >
            <!-- Indeterminate shimmer -->
            <div
                v-if="isIndeterminate"
                class="absolute inset-0 -translate-x-full animate-[shimmer_1.4s_ease-in-out_infinite] rounded-full bg-primary/40"
            />
            <!-- Determinate fill -->
            <div
                v-else
                class="h-full rounded-full bg-primary transition-all duration-500 ease-out"
                :style="{ width: `${percent}%` }"
            />
        </div>

        <!-- Counter -->
        <p v-if="hasCounter" class="text-right text-xs text-muted-foreground">
            {{ progress!.current ?? 0 }} / {{ progress!.total }}
        </p>
        <p
            v-else-if="!isIndeterminate && percent > 0"
            class="text-right text-xs text-muted-foreground"
        >
            {{ percent }} %
        </p>
    </div>
</template>
